import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { slides } from "../mock/mock";
import SmartImage from "./SmartImage";

export default function Hero({ onApply }) {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  const go = (n) => setIdx((n + slides.length) % slides.length);

  useEffect(() => {
    timer.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(timer.current);
  }, []);

  const restart = (fn) => {
    clearInterval(timer.current);
    fn();
    timer.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
  };

  return (
    <section id="home" className="relative min-h-[92vh] overflow-hidden" style={{ background: "var(--teal-950)" }}>
      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0"
          style={{
            opacity: i === idx ? 1 : 0,
            transition: "opacity 1.1s ease",
            pointerEvents: "none",
          }}
        >
          <SmartImage
            src={s.image}
            fallback={s.fallback}
            alt={s.title}
            className={`h-full w-full object-cover ${i === idx ? "kenburns" : ""}`}
          />
        </div>
      ))}

      {/* Overlays */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(100deg, rgba(5,32,29,.90) 0%, rgba(5,32,29,.66) 45%, rgba(5,32,29,.20) 100%)",
        }}
      />
      <div className="grain absolute inset-0" />

      {/* Content */}
      <div className="relative mx-auto flex min-h-[92vh] max-w-[1280px] flex-col justify-center px-5 pb-28 pt-24 lg:px-6">
        <div key={idx} className="fade-up max-w-[820px]">
          <div className="flex items-center gap-3">
            <span className="h-[6px] w-[6px] rotate-45" style={{ background: "var(--gold-400)" }} />
            <span className="eyebrow" style={{ color: "var(--gold-200)" }}>
              {slides[idx].kicker}
            </span>
          </div>

          <h1
            className="font-display mt-7 text-[2.6rem] leading-[1.04] tracking-tight sm:text-[3.6rem] lg:text-[4.6rem]"
            style={{ color: "#FBF7F0", fontWeight: 300 }}
          >
            {slides[idx].title}
          </h1>

          <p className="mt-6 text-[0.95rem] tracking-wide" style={{ color: "rgba(235,214,168,.9)" }}>
            — {slides[idx].author}
          </p>

          <div className="mt-11 flex flex-wrap items-center gap-4">
            <button
              onClick={onApply}
              className="group flex items-center gap-3 rounded-full px-8 py-4 text-[0.88rem] font-semibold"
              style={{
                background: "var(--gold)",
                color: "#fff",
                transition: "transform .3s ease, box-shadow .3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 18px 34px -16px rgba(192,138,46,.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Apply Now
              <ArrowRight size={17} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </button>

            <Link
              to="/campus"
              className="rounded-full px-8 py-4 text-[0.88rem] font-semibold"
              style={{
                border: "1px solid rgba(251,247,240,.38)",
                color: "#FBF7F0",
                transition: "background .3s ease, border-color .3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(251,247,240,.12)";
                e.currentTarget.style.borderColor = "rgba(251,247,240,.7)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(251,247,240,.38)";
              }}
            >
              Explore the Campus
            </Link>
          </div>

          <div className="mt-10 flex items-center gap-2 text-[0.82rem]" style={{ color: "rgba(251,247,240,.6)" }}>
            <MapPin size={14} /> Arasur, Coimbatore · 160+ acre campus
          </div>
        </div>

        {/* Controls */}
        <div className="absolute bottom-10 left-5 right-5 flex items-end justify-between lg:left-6 lg:right-6">
          <div className="flex items-center gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                aria-label={`Slide ${i + 1}`}
                onClick={() => restart(() => go(i))}
                className="h-[3px] rounded-full"
                style={{
                  width: i === idx ? 44 : 20,
                  background: i === idx ? "var(--gold-400)" : "rgba(251,247,240,.35)",
                  transition: "width .4s ease, background .4s ease",
                }}
              />
            ))}
          </div>
          <div className="hidden gap-3 sm:flex">
            {[ChevronLeft, ChevronRight].map((Icon, k) => (
              <button
                key={k}
                aria-label={k ? "Next slide" : "Previous slide"}
                onClick={() => restart(() => go(idx + (k ? 1 : -1)))}
                className="grid h-11 w-11 place-items-center rounded-full"
                style={{
                  border: "1px solid rgba(251,247,240,.3)",
                  color: "#FBF7F0",
                  transition: "background .3s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(251,247,240,.14)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
